import { HttpBackend, HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ConfigService } from './config.service';
import { COLLECTOR_ROLES, COMPANY_ROLES, type LoginResponse, type Profile, type Role, STAFF_ROLES } from './models';

const REFRESH_KEY = 'so.refreshToken';

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* storage unavailable (private mode) - session ends on reload */
  }
}

/**
 * Short-lived access token kept in memory only; the refresh token is rotated
 * by the API on every use. A single refresh runs at a time.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly config = inject(ConfigService);
  private readonly router = inject(Router);
  // Raw client without interceptors, so refresh calls never loop.
  private readonly http = new HttpClient(inject(HttpBackend));

  private accessToken: string | null = null;
  private refreshing: Promise<boolean> | null = null;

  readonly user = signal<Profile | null>(null);
  readonly isLoggedIn = computed(() => this.user() !== null);
  readonly role = computed<Role | null>(() => this.user()?.role ?? null);
  readonly isStaff = computed(() => STAFF_ROLES.includes(this.role() as Role));
  readonly isAdmin = computed(() => this.role() === 'Admin');
  readonly isCompany = computed(() => COMPANY_ROLES.includes(this.role() as Role));
  readonly isCollector = computed(() => COLLECTOR_ROLES.includes(this.role() as Role));
  /** Who may open the admin panel at all. */
  readonly canUsePanel = computed(() => {
    const u = this.user();
    return !!u && (this.isStaff() || this.isCompany() || this.isCollector() || u.allowDashboard);
  });
  readonly canSeeDashboard = computed(() => this.isStaff() || this.isCompany() || !!this.user()?.allowDashboard);

  get token(): string | null {
    return this.accessToken;
  }

  hasRole(...roles: Role[]): boolean {
    const r = this.role();
    return !!r && roles.includes(r);
  }

  private url(path: string) {
    return `${this.config.apiUrl}${path}`;
  }

  private accept(res: LoginResponse) {
    this.accessToken = res.accessToken;
    writeStorage(REFRESH_KEY, res.refreshToken);
    if (res.user) this.user.set(res.user);
  }

  async login(email: string, password: string, lang: string): Promise<void> {
    const res = await firstValueFrom(
      this.http.post<LoginResponse>(this.url('/api/v1/auth/login'), { email, password }, { headers: { 'Accept-Language': lang } }),
    );
    this.accept(res);
  }

  /** Restores the session on page load using the stored refresh token. */
  async restore(): Promise<void> {
    if (!readStorage(REFRESH_KEY)) return;
    if (await this.refresh()) await this.loadProfile();
  }

  async loadProfile(): Promise<void> {
    if (!this.accessToken) return;
    const me = await firstValueFrom(
      this.http.get<Profile>(this.url('/api/v1/me'), { headers: { Authorization: `Bearer ${this.accessToken}` } }),
    );
    this.user.set(me);
  }

  refresh(): Promise<boolean> {
    this.refreshing ??= (async () => {
      const refreshToken = readStorage(REFRESH_KEY);
      if (!refreshToken) return false;
      try {
        const res = await firstValueFrom(this.http.post<LoginResponse>(this.url('/api/v1/auth/refresh'), { refreshToken }));
        this.accept(res);
        return true;
      } catch {
        this.clear();
        return false;
      } finally {
        this.refreshing = null;
      }
    })();
    return this.refreshing;
  }

  async logout(): Promise<void> {
    const refreshToken = readStorage(REFRESH_KEY);
    if (refreshToken) {
      try {
        await firstValueFrom(this.http.post(this.url('/api/v1/auth/logout'), { refreshToken }));
      } catch {
        /* ignore */
      }
    }
    this.clear();
    await this.router.navigate(['/login']);
  }

  clear() {
    this.accessToken = null;
    writeStorage(REFRESH_KEY, null);
    this.user.set(null);
  }
}
